import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { useTeam } from '../context/TeamContext';
import { API_URL } from '../lib/apiUrl';

/**
 * RemindersPanel
 * -----------------------------------------------------------------------------
 * Muestra los avisos accionables del entrenador (GET /api/reminders).
 *
 * Los avisos se calculan en el backend a partir de los datos reales del usuario
 * (partido próximo, convocatoria pendiente, sesión de hoy/mañana, semana sin
 * planificar, post-partido sin analizar). No dependen de push ni de un cron:
 * se recalculan en cada carga, así que nunca "se pierden".
 *
 * Si no hay avisos o falla la petición, el panel no se muestra (no molesta).
 */
const PRIORITY = {
  high:   { color: '#ff4d6d', label: 'Urgente' },
  medium: { color: '#ffd34d', label: 'Pendiente' },
  low:    { color: '#4dd6ff', label: 'Info' },
};

const VISIBLE_BY_DEFAULT = 3;

export default function RemindersPanel() {
  const navigate = useNavigate();
  const { currentTeam } = useTeam();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // Los avisos se piden para el equipo activo, así son siempre relevantes.
  // Al cambiar de equipo se recalculan solos.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const q = currentTeam?.id ? `?team_id=${currentTeam.id}` : '';
    axios.get(`${API_URL}/api/reminders${q}`)
      .then(r => { if (alive) setReminders(r.data?.reminders || []); })
      .catch(() => { /* silencioso: es un extra, no debe romper el dashboard */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [currentTeam?.id]);

  if (loading || reminders.length === 0) return null;

  const shown = expanded ? reminders : reminders.slice(0, VISIBLE_BY_DEFAULT);
  const hidden = reminders.length - shown.length;
  const urgent = reminders.filter(r => r.priority === 'high').length;

  return (
    <div className="rounded-2xl p-5" style={{ background: 'var(--surface-0)', border: '1px solid var(--border-subtle)' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4" style={{ color: 'var(--primary)' }} />
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--primary)' }}>
            Tus avisos
          </p>
          {urgent > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(255,77,109,0.15)', color: 'var(--danger)' }}>
              {urgent} urgente{urgent > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {shown.map((r, i) => {
          const p = PRIORITY[r.priority] || PRIORITY.low;
          return (
            <button
              key={`${r.type}-${i}`}
              onClick={() => r.action_path && navigate(r.action_path)}
              className="w-full text-left flex items-start gap-3 p-3 rounded-xl transition-all"
              style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = `${p.color}50`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2"
                style={{ background: p.color, boxShadow: `0 0 8px ${p.color}` }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: 'var(--text-0)' }}>{r.title}</p>
                {r.detail && <p className="text-xs mt-0.5" style={{ color: 'var(--text-2)' }}>{r.detail}</p>}
              </div>
              {r.action_label && (
                <span className="flex items-center gap-1 text-[11px] font-semibold flex-shrink-0 mt-0.5"
                  style={{ color: p.color }}>
                  <span className="hidden sm:inline">{r.action_label}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {(hidden > 0 || expanded) && (
        <button onClick={() => setExpanded(v => !v)}
          className="mt-3 flex items-center gap-1 text-xs font-semibold"
          style={{ color: 'var(--text-2)' }}>
          {expanded
            ? <><ChevronUp className="w-3.5 h-3.5" /> Ver menos</>
            : <><ChevronDown className="w-3.5 h-3.5" /> Ver {hidden} aviso{hidden > 1 ? 's' : ''} más</>}
        </button>
      )}
    </div>
  );
}
